import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, TextField, InputAdornment, Pagination,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Divider, Select, MenuItem, FormControl, InputLabel, CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CloseIcon from "@mui/icons-material/Close";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const PURPLE = "#244C77";
const SECONDARY = "#4A7BA8";

const FALLBACK_STUDENTS = [
  {
    id: "1", studentName: "Arjun Rajan", registrationNo: "CTS-2025-001",
    grade: "Nilai 4", section: "A", evaluationDate: "2025-01-10", paymentDate: "2025-01-12",
    evaluationStatus: "Completed", paymentStatus: "Verified",
    parentName: "Rajan Murugan", email: "rajan@example.com", phone: "404-555-0101",
    dateOfBirth: "2015-03-22", gender: "Male", academicYear: "2025-26"
  },
  {
    id: "2", studentName: "Priya Sharma", registrationNo: "CTS-2025-002",
    grade: "Nilai 6", section: "B", evaluationDate: "2025-01-14", paymentDate: "2025-01-16",
    evaluationStatus: "Completed", paymentStatus: "Verified",
    parentName: "Sharma Venkat", email: "sharma@example.com", phone: "404-555-0102",
    dateOfBirth: "2013-07-15", gender: "Female", academicYear: "2025-26"
  },
  {
    id: "3", studentName: "Karthik Mani", registrationNo: "CTS-2025-003",
    grade: "Mazhalai", section: "A", evaluationDate: "2025-01-18", paymentDate: "2025-01-19",
    evaluationStatus: "Completed", paymentStatus: "Verified",
    parentName: "Mani Kumar", email: "mani@example.com", phone: "404-555-0103",
    dateOfBirth: "2019-11-05", gender: "Male", academicYear: "2025-26"
  },
];

const GRADES = ["All", "MunMazhalai", "Mazhalai", "Nilai 1", "Nilai 2", "Nilai 3",
  "Nilai 4", "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"];
const SECTIONS = ["All", "A", "B", "C", "D"];
const ACADEMIC_YEARS = ["All", "2024-25", "2025-26", "2026-27"];
const ROWS_PER_PAGE = 10;

const PrincipalRegistrations = () => {
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [gradeFilter, setGradeFilter]         = useState("All");
  const [sectionFilter, setSectionFilter]     = useState("All");
  const [yearFilter, setYearFilter]           = useState("All");
  const [page, setPage]             = useState(1);
  const [detailDialog, setDetailDialog]       = useState({ open: false, student: null });
  const [actionDialog, setActionDialog]       = useState({ open: false, student: null, action: "", remarks: "" });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]           = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData("principal/registrations?evaluationStatus=completed&paymentStatus=verified");
      const data = res?.data?.registrations || res?.data || res || [];
      setRows(Array.isArray(data) && data.length > 0 ? data : FALLBACK_STUDENTS);
    } catch (_) {
      setRows(FALLBACK_STUDENTS);
      setToast({ open: true, severity: "warning", message: "Showing demo data — API unavailable" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r => {
    const search = filterText.toLowerCase();
    const matchText = !search ||
      (r.studentName || "").toLowerCase().includes(search) ||
      (r.registrationNo || "").toLowerCase().includes(search) ||
      (r.grade || "").toLowerCase().includes(search);
    const matchGrade   = gradeFilter === "All"   || r.grade    === gradeFilter;
    const matchSection = sectionFilter === "All" || r.section  === sectionFilter;
    const matchYear    = yearFilter === "All"    || r.academicYear === yearFilter;
    return matchText && matchGrade && matchSection && matchYear;
  });

  const pageCount = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const pageRows  = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const handleAction = async () => {
    const { student, action, remarks } = actionDialog;
    if (!student) return;
    setSubmitting(true);
    try {
      await putData(`principal/registration/${student.id}/approve`, {
        action,      // "approve" | "reject"
        remarks,
      });
      setToast({ open: true, severity: "success", message: `Registration ${action === "approve" ? "approved" : "rejected"} successfully!` });
      setActionDialog({ open: false, student: null, action: "", remarks: "" });
      setDetailDialog({ open: false, student: null });
      load();
    } catch (err) {
      setToast({ open: true, severity: "error", message: err?.response?.data?.message || "Action failed. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const DetailField = ({ label, value }) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: "11px", color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "14px", color: "#111827", fontWeight: 500 }}>
        {value || "—"}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 3, fontFamily: "'Inter', 'Roboto', sans-serif" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <Typography variant="body2" color="text.secondary" mb={0.5}>Pages / Registrations</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AssignmentTurnedInIcon sx={{ color: PURPLE, fontSize: "28px" }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "22px", color: "#111827" }}>Student Registrations</Typography>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              Completed evaluation &amp; verified payment — awaiting Principal approval
            </Typography>
          </Box>
          <Chip
            label={`${filtered.length} records`}
            size="small"
            sx={{ backgroundColor: "#EAF1F8", color: PURPLE, fontWeight: 600, fontSize: "12px" }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<FilterListIcon />}
            size="small"
            onClick={() => setShowFilter(v => !v)}
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}
          >
            Filters
          </Button>
          <Button
            startIcon={<FileDownloadOutlinedIcon />}
            size="small"
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Filter Panel */}
      {showFilter && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: "1px solid #E5E7EB" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                size="small" fullWidth
                placeholder="Search by name, reg no, grade..."
                value={filterText} onChange={e => { setFilterText(e.target.value); setPage(1); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Grade</InputLabel>
                <Select value={gradeFilter} onChange={e => { setGradeFilter(e.target.value); setPage(1); }} label="Grade" sx={{ borderRadius: "8px" }}>
                  {GRADES.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Section</InputLabel>
                <Select value={sectionFilter} onChange={e => { setSectionFilter(e.target.value); setPage(1); }} label="Section" sx={{ borderRadius: "8px" }}>
                  {SECTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Academic Year</InputLabel>
                <Select value={yearFilter} onChange={e => { setYearFilter(e.target.value); setPage(1); }} label="Academic Year" sx={{ borderRadius: "8px" }}>
                  {ACADEMIC_YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: PURPLE }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ background: `linear-gradient(135deg, ${PURPLE} 0%, ${SECONDARY} 100%)` }}>
                  <TableRow>
                    {["#", "Student Name", "Registration No", "Grade", "Section", "Evaluation Date", "Payment Date", "Actions"].map(h => (
                      <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "12px", whiteSpace: "nowrap" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: "center", py: 6, color: "#8F95B2", fontSize: "14px" }}>
                        No registrations found with completed evaluation and verified payment
                      </TableCell>
                    </TableRow>
                  ) : pageRows.map((r, idx) => (
                    <TableRow key={r.id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" } }}>
                      <TableCell sx={{ fontSize: "13px", color: "#6B7280" }}>{(page - 1) * ROWS_PER_PAGE + idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{r.studentName}</TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#6B7280" }}>{r.registrationNo}</TableCell>
                      <TableCell>
                        <Chip label={r.grade} size="small" sx={{ backgroundColor: "#EAF1F8", color: PURPLE, fontSize: "11px" }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "13px", color: "#374151" }}>{r.section || "—"}</TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#374151" }}>
                        {r.evaluationDate ? new Date(r.evaluationDate).toLocaleDateString("en-GB") : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#374151" }}>
                        {r.paymentDate ? new Date(r.paymentDate).toLocaleDateString("en-GB") : "—"}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => setDetailDialog({ open: true, student: r })}
                            sx={{ color: PURPLE, "&:hover": { backgroundColor: "#EAF1F8" } }}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {pageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Pagination
                  count={pageCount} page={page} onChange={(_, v) => setPage(v)} size="small"
                  sx={{ "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: PURPLE, color: "#fff" } }}
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, student: null })}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ background: `linear-gradient(135deg, ${PURPLE} 0%, ${SECONDARY} 100%)`, px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "17px" }}>Student Registration Details</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "12px" }}>
                {detailDialog.student?.registrationNo}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setDetailDialog({ open: false, student: null })} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {detailDialog.student && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#374151", mb: 1.5 }}>Personal Information</Typography>
                <DetailField label="Full Name"    value={detailDialog.student.studentName} />
                <DetailField label="Date of Birth" value={detailDialog.student.dateOfBirth ? new Date(detailDialog.student.dateOfBirth).toLocaleDateString("en-GB") : "—"} />
                <DetailField label="Gender"       value={detailDialog.student.gender} />
                <DetailField label="Academic Year" value={detailDialog.student.academicYear} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#374151", mb: 1.5 }}>Academic Information</Typography>
                <DetailField label="Registration No" value={detailDialog.student.registrationNo} />
                <DetailField label="Grade"           value={detailDialog.student.grade} />
                <DetailField label="Section"         value={detailDialog.student.section} />
                <DetailField label="Evaluation Date" value={detailDialog.student.evaluationDate ? new Date(detailDialog.student.evaluationDate).toLocaleDateString("en-GB") : "—"} />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#374151", mb: 1.5 }}>Parent / Guardian</Typography>
                <DetailField label="Parent Name" value={detailDialog.student.parentName} />
                <DetailField label="Email"       value={detailDialog.student.email} />
                <DetailField label="Phone"       value={detailDialog.student.phone} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#374151", mb: 1.5 }}>Status</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={`Evaluation: ${detailDialog.student.evaluationStatus}`} size="small"
                    sx={{ backgroundColor: "#f0fdf4", color: "#16a34a", fontWeight: 600 }} />
                  <Chip label={`Payment: ${detailDialog.student.paymentStatus}`} size="small"
                    sx={{ backgroundColor: "#EAF1F8", color: PURPLE, fontWeight: 600 }} />
                </Box>
                <Typography sx={{ fontSize: "12px", color: "#9CA3AF", mt: 2 }}>
                  Payment verified on: {detailDialog.student.paymentDate ? new Date(detailDialog.student.paymentDate).toLocaleDateString("en-GB") : "—"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setDetailDialog({ open: false, student: null })}
            sx={{ border: "1.5px solid #D1D5DB", borderRadius: "10px", textTransform: "none", color: "#374151", px: 3 }}
          >
            Close
          </Button>
          <Button
            startIcon={<CancelOutlinedIcon />}
            onClick={() => setActionDialog({ open: true, student: detailDialog.student, action: "reject", remarks: "" })}
            sx={{ border: "1.5px solid #fca5a5", borderRadius: "10px", textTransform: "none", color: "#dc2626", px: 3 }}
          >
            Reject
          </Button>
          <Button
            startIcon={<CheckCircleOutlineIcon />}
            variant="contained"
            onClick={() => setActionDialog({ open: true, student: detailDialog.student, action: "approve", remarks: "" })}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, backgroundColor: "#16a34a", boxShadow: "none", px: 3, "&:hover": { backgroundColor: "#15803d" } }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, student: null, action: "", remarks: "" })}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px" }}>
          {actionDialog.action === "approve" ? "Approve Registration" : "Reject Registration"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", color: "#374151", mb: 1.5 }}>
            Student: <strong>{actionDialog.student?.studentName}</strong>
          </Typography>
          <TextField
            fullWidth size="small" multiline rows={3}
            label="Remarks (optional)"
            value={actionDialog.remarks}
            onChange={e => setActionDialog({ ...actionDialog, remarks: e.target.value })}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setActionDialog({ open: false, student: null, action: "", remarks: "" })}
            fullWidth
            sx={{ border: "1.5px solid #D1D5DB", borderRadius: "10px", textTransform: "none", color: "#374151" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAction}
            fullWidth variant="contained"
            disabled={submitting}
            sx={{
              borderRadius: "10px", textTransform: "none", fontWeight: 600, boxShadow: "none",
              backgroundColor: actionDialog.action === "approve" ? "#16a34a" : "#dc2626",
              "&:hover": { backgroundColor: actionDialog.action === "approve" ? "#15803d" : "#b91c1c" }
            }}
          >
            {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : (actionDialog.action === "approve" ? "Approve" : "Reject")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrincipalRegistrations;
